'use memo'

import {
  AgxButton,
  AgxDropdown,
  AgxSegmentPicker,
  AgxSpacer,
  AgxTable,
  AgxToggleSwitch,
  AgxTypography,
  RowData,
  useToast,
} from '@urbanx/agx.ui-components2'
import LoadingState from 'components/loadingState/LoadingState'
import Panel from 'components/panel/Panel'
import {
  FeatureToggles as FeatureTogglesModel,
  LicenceDetailsState,
  useGetAllAgencies,
  useInvalidateUseStaffGetFeatureToggleManagement,
  useStaffGetFeatureToggleManagement,
  useStaffUpdateFeatureToggleManagement,
} from '@urbanx/agx.agencies.client'
import { useEffect, useState } from 'react'

import {
  cloneFeatureToggles,
  createEmptyFeatureToggles,
  featureDefinitions,
  FeatureKey,
  FeatureLevel,
  FeatureRows,
  FeatureState,
  getAgencyOptions,
  getAgencyStates,
  getRawFeatures,
  getSelectedAgency,
  levelSegments,
  NestedFeatureRows,
  pruneBlankRows,
  stateOptions,
  tableColumns,
} from './helpers'
import './FeatureToggles.scss'

const FeatureToggles = () => {
  const { pushToast } = useToast()

  const [selectedLevel, setSelectedLevel] = useState<FeatureLevel>('global')
  const [selectedState, setSelectedState] = useState<FeatureState>(
    LicenceDetailsState.QLD
  )
  const [selectedAgencyId, setSelectedAgencyId] = useState('')
  const [draft, setDraft] = useState<FeatureTogglesModel>(
    createEmptyFeatureToggles()
  )

  const { data: featureToggles, isPending: isLoadingFeatures } =
    useStaffGetFeatureToggleManagement()

  const { data: agencies, isPending: isLoadingAgencies } = useGetAllAgencies()

  useEffect(() => {
    setDraft(cloneFeatureToggles(featureToggles))
  }, [featureToggles])

  const baseline = cloneFeatureToggles(featureToggles)
  const prunedBaseline = pruneBlankRows(baseline)
  const prunedDraft = pruneBlankRows(draft)
  const hasUnsavedChanges =
    JSON.stringify(prunedDraft) !== JSON.stringify(prunedBaseline)

  const selectedAgency =
    selectedLevel === 'agency'
      ? getSelectedAgency(agencies, selectedAgencyId)
      : undefined
  const agencyOptions = getAgencyOptions(agencies)
  const editableStates =
    selectedLevel === 'global'
      ? [selectedState]
      : getAgencyStates(selectedAgency)

  const scopeComplete =
    selectedLevel === 'global'
      ? !!selectedState
      : !!selectedAgencyId && editableStates.length > 0

  const invalidateFeatureToggleManagement =
    useInvalidateUseStaffGetFeatureToggleManagement()

  const {
    staffUpdateFeatureToggleManagement: saveFeatureToggles,
    staffUpdateFeatureToggleManagementPending: isSaving,
  } = useStaffUpdateFeatureToggleManagement({
    onSuccess: async () => {
      await invalidateFeatureToggleManagement()
      pushToast({
        message: 'Feature toggles saved',
        type: 'success',
      })
    },
    onError: () => {
      pushToast({
        message: 'Error saving feature toggles',
        type: 'error',
      })
    },
  })

  const setFeatureValue = (
    state: FeatureState,
    featureKey: FeatureKey,
    value: boolean
  ) => {
    if (!scopeComplete) return

    setDraft((previousDraft) => {
      const nextDraft = cloneFeatureToggles(previousDraft)
      const nextFeatures = {
        ...getRawFeatures(nextDraft, selectedLevel, state, selectedAgencyId),
        [featureKey]: value,
      }

      if (selectedLevel === 'global') {
        const rows = nextDraft.globalFeatures as FeatureRows
        rows[state] = nextFeatures
        return nextDraft
      }

      const rows = nextDraft.agencyFeatures as NestedFeatureRows

      rows[selectedAgencyId] = {
        ...(rows[selectedAgencyId] ?? {}),
        [state]: nextFeatures,
      }

      return nextDraft
    })
  }

  const getRows = (state: FeatureState): RowData[] => {
    const selectedFeatures = getRawFeatures(
      draft,
      selectedLevel,
      state,
      selectedAgencyId
    )

    return featureDefinitions.map((feature) => {
      const value = selectedFeatures[feature.key] === true

      return {
        dataTestId: `featureToggle-row-${state}-${feature.key}`,
        Feature: (
          <AgxTypography
            id={`feature-${state}-${feature.key}`}
            dataTestId={`featureToggle-label-${state}-${feature.key}`}
            type="body_ui"
            size="small"
          >
            {feature.label}
          </AgxTypography>
        ),
        Enabled: (
          <div className="featureTogglesPage__switchCell">
            <AgxToggleSwitch
              id={`featureToggle-${state}-${feature.key}`}
              dataTestId={`featureToggle-switch-${state}-${feature.key}`}
              inputDataTestId={`featureToggle-checkbox-${state}-${feature.key}`}
              checked={value}
              disabled={!scopeComplete || isSaving}
              onChange={(isChecked: boolean) =>
                setFeatureValue(state, feature.key, isChecked)
              }
            />
          </div>
        ),
        'Current value': (
          <AgxTypography
            id={`featureCurrentValue-${state}-${feature.key}`}
            dataTestId={`featureToggle-currentValue-${state}-${feature.key}`}
            type="body_ui_strong"
            size="small"
            color={value ? 'var(--success-700)' : 'var(--text-secondary)'}
          >
            {value ? 'On' : 'Off'}
          </AgxTypography>
        ),
      }
    })
  }

  const isLoading = isLoadingFeatures || isLoadingAgencies

  const onLevelChange = (value: string) => {
    const nextLevel = value as FeatureLevel
    if (nextLevel === selectedLevel) return

    setSelectedLevel(nextLevel)
    setSelectedAgencyId('')
  }

  return (
    <div className="featureTogglesPage">
      <AgxSpacer
        direction="vertical"
        gap="spacing-2xl"
        fill
        padding="spacing-4xl"
      >
        <AgxSpacer
          direction="horizontal"
          fill
          contentJustification="space-between"
          contentAlignment="center"
        >
          <AgxSpacer direction="vertical" gap="spacing-xs">
            <AgxTypography
              id="featureTogglesHeading"
              type="heading"
              size="large"
            >
              Feature Toggles
            </AgxTypography>
            <AgxTypography
              id="featureTogglesSubheading"
              type="body"
              size="medium"
            >
              Existing row-based feature controls
            </AgxTypography>
          </AgxSpacer>

          <AgxSpacer
            direction="horizontal"
            gap="spacing-md"
            contentAlignment="center"
            style={{
              width: 'min-content',
            }}
          >
            <AgxButton
              id="resetFeatureToggles"
              text="Reset"
              variant="hollow"
              size="medium"
              disabled={!hasUnsavedChanges || isSaving}
              onClick={() => setDraft(cloneFeatureToggles(baseline))}
            />
            <AgxButton
              id="saveFeatureToggles"
              text={isSaving ? 'Saving' : 'Save'}
              variant="primary"
              size="medium"
              disabled={!hasUnsavedChanges || isSaving}
              onClick={() => saveFeatureToggles(prunedDraft)}
            />
          </AgxSpacer>
        </AgxSpacer>

        <Panel title="Scope">
          <AgxSpacer direction="vertical" gap="spacing-xl" fill>
            <AgxSegmentPicker
              id="featureToggleLevel"
              key={selectedLevel}
              segments={levelSegments}
              defaultValue={selectedLevel}
              onChange={onLevelChange}
            />

            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              fill
              className="featureTogglesPage__selectors"
              style={{ maxWidth: '400px' }}
            >
              {selectedLevel === 'global' && (
                <AgxDropdown
                  id="featureToggleState"
                  label="State"
                  selected={selectedState}
                  options={stateOptions}
                  onChange={(value) => setSelectedState(value as FeatureState)}
                  stretch
                  hideOptionalText
                />
              )}

              {selectedLevel === 'agency' && (
                <AgxDropdown
                  id="featureToggleAgency"
                  triggerDataTestId="featureToggleAgency-selected"
                  label="Agency"
                  selected={selectedAgencyId}
                  options={agencyOptions}
                  onChange={setSelectedAgencyId}
                  placeholder="Select agency"
                  searchable
                  searchPlaceholder="Search agencies"
                  stretch
                  hideOptionalText
                />
              )}
            </AgxSpacer>
          </AgxSpacer>
        </Panel>

        <Panel title="Features">
          <AgxSpacer direction="vertical" gap="spacing-lg" fill>
            {!scopeComplete && (
              <AgxTypography
                id="featureToggleScopeRequired"
                type="body"
                size="small"
              >
                Select an agency to edit feature toggles.
              </AgxTypography>
            )}

            {scopeComplete &&
              editableStates.map((state) => (
                <AgxSpacer
                  key={state}
                  direction="vertical"
                  gap="spacing-md"
                  fill
                  className="featureTogglesPage__stateGroup"
                >
                  {selectedLevel !== 'global' && (
                    <AgxTypography
                      id={`featureToggleStateHeading-${state}`}
                      type="heading"
                      size="small"
                    >
                      {state}
                    </AgxTypography>
                  )}
                  <AgxTable
                    columns={tableColumns}
                    rows={getRows(state)}
                    showLoadingState={isLoading}
                    tableStyle="square"
                  />
                </AgxSpacer>
              ))}
            {isLoading && <LoadingState />}
          </AgxSpacer>
        </Panel>
      </AgxSpacer>
    </div>
  )
}

export default FeatureToggles
